import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Lock, 
  Unlock,
  Building2,
  MapPin,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import LogoUpload from '../common/LogoUpload';
import siteService from '../../services/sites';
import PermissionButton from '../permissions/PermissionButton';
import { PERMISSIONS } from '@/types/permissions';

interface Tenant {
  id: number;
  name: string;
  code: string;
  is_active: boolean;
  is_blocked: boolean;
}

interface Site {
  id: number;
  name: string;
  code: string;
  site_type: string;
  timezone: string;
  address: string;
  contact_phone: string;
  contact_email: string;
  is_active: boolean;
  tenant: number;
  tenant_name?: string;
  logo?: string;
  is_blocked: boolean;
  blocked_at?: string;
  blocked_by?: number;
  blocked_by_name?: string;
  block_reason?: string;
}

interface FormData {
  name: string;
  code: string;
  site_type: string;
  timezone: string;
  address: string;
  contact_phone: string;
  contact_email: string;
  tenant: number; // ID du tenant
}

interface SiteManagementProps {
  sites: Site[];
  tenants: Tenant[];
  onSitesUpdate: () => Promise<void>;
}

const SiteManagement: React.FC<SiteManagementProps> = ({ sites, tenants, onSitesUpdate }) => {
  // Fonction utilitaire pour extraire les tableaux des props
  const extractArrayFromProp = (prop: any, propName: string): any[] => {
    if (Array.isArray(prop)) {
      return prop;
    }
    
    if (prop && typeof prop === 'object') {
      // Essayer différentes structures communes
      if (prop.results && Array.isArray(prop.results)) {
        return prop.results;
      }
      if (prop.data && Array.isArray(prop.data)) {
        return prop.data;
      }
      if (prop.items && Array.isArray(prop.items)) {
        return prop.items;
      }
      if (prop.list && Array.isArray(prop.list)) {
        return prop.list;
      }
      
      // Si c'est un objet avec des clés numériques, essayer de le convertir
      const keys = Object.keys(prop);
      if (keys.length > 0 && keys.every(key => !isNaN(Number(key)))) {
        const array = Object.values(prop);
        
        return array;
      }
    }
    
    console.warn(`⚠️ ${propName}: Impossible d'extraire un tableau, utilisation d'un tableau vide`);
    return [];
  };

  // Extraire les tableaux des props
  const sitesArray = extractArrayFromProp(sites, 'sites');
  const tenantsArray = extractArrayFromProp(tenants, 'tenants');

  // Vérification de sécurité pour les tableaux vides
  if (tenantsArray.length === 0) {
    console.warn('⚠️ SiteManagement: Aucun tenant disponible');
    return (
      <div className="text-center py-12">
        <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun tenant disponible</h3>
        <p className="text-gray-600">
          Impossible de créer des sites sans tenant. Veuillez d'abord créer des tenants.
        </p>
      </div>
    );
  }

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingSite, setEditingSite] = useState<Site | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBlockModal, setShowBlockModal] = useState(false);
  const [showUnblockModal, setShowUnblockModal] = useState(false);
  const [selectedSite, setSelectedSite] = useState<Site | null>(null);
  const [blockReason, setBlockReason] = useState('');
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    code: '',
    site_type: 'STANDARD',
    timezone: 'Africa/Abidjan',
    address: '',
    contact_phone: '',
    contact_email: '',
    tenant: 0  // Sera mis à jour après le chargement des tenants
  });

  // État pour le logo
  const [, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);



  // Fonction de gestion du logo
  const handleLogoChange = (file: File | null, logoUrl?: string) => {
    setLogoFile(file);
    setLogoPreview(logoUrl || null);
  };

  // Fonction de réinitialisation du formulaire
  const resetForm = () => {
    setFormData({
      name: '',
      code: '',
      site_type: 'STANDARD',
      timezone: 'Africa/Abidjan',
      address: '',
      contact_phone: '',
      contact_email: '',
      tenant: tenantsArray.length > 0 ? tenantsArray[0].id : 0  // Premier tenant valide ou 0
    });
    setLogoFile(null);
    setLogoPreview(null);
  };

  // Initialiser automatiquement le premier tenant valide si aucun n'est sélectionné
  useEffect(() => {
    if (tenantsArray.length > 0 && formData.tenant === 0) {
      const activeTenants = tenantsArray.filter(tenant => tenant.is_active && !tenant.is_blocked);
      if (activeTenants.length > 0) {
        setFormData(prev => ({ ...prev, tenant: activeTenants[0].id }));
      }
    }
  }, [tenantsArray, formData.tenant]);

  const handleCreateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation du tenant
    if (!formData.tenant || formData.tenant === 0) {
      alert('Veuillez sélectionner un tenant');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const selectedTenant = tenantsArray.find((t) => t.id === formData.tenant);
      if (!selectedTenant) {
        alert('Veuillez sélectionner un tenant valide');
        return;
      }

      const siteData = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        site_type: formData.site_type,
        timezone: formData.timezone,
        address: formData.address || '',
        contact_phone: formData.contact_phone || '',
        contact_email: formData.contact_email || '',
        params_pont: {},
        params_reseau: {},
        tenant_code: selectedTenant.code,
        tenant: selectedTenant.id,
      };
      
      const response = await siteService.createSite(siteData);
      
      if (response) {
        setShowCreateForm(false);
        resetForm();
        onSitesUpdate();
      } else {
        alert('Erreur lors de la création du site');
      }
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(`Erreur lors de la création du site: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateSite = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation du tenant
    if (!editingSite || !formData.tenant || formData.tenant === 0) {
      alert('Veuillez sélectionner un tenant');
      return;
    }
    
    setIsSubmitting(true);
    try {
      const selectedTenant = tenantsArray.find((t) => t.id === formData.tenant);
      if (!selectedTenant) {
        alert('Veuillez sélectionner un tenant valide');
        return;
      }

      const siteData = {
        name: formData.name.trim(),
        code: formData.code.trim().toUpperCase(),
        site_type: formData.site_type,
        timezone: formData.timezone,
        address: formData.address || '',
        contact_phone: formData.contact_phone || '',
        contact_email: formData.contact_email || '',
        tenant: selectedTenant.id,
      };
      
      const response = await siteService.updateSite(editingSite.id, siteData);
      
      if (response) {
        setEditingSite(null);
        resetForm();
        onSitesUpdate();
      } else {
        alert('Erreur lors de la modification du site');
      }
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(`Erreur lors de la modification du site: ${error.message || 'Erreur inconnue'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteSite = async (siteId: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce site ?')) {
      try {
        const success = await siteService.deleteSite(siteId);
        
        if (success) {
          onSitesUpdate();
        } else {
          alert('Erreur lors de la suppression');
        }
      } catch (error: any) {
        console.error('Erreur:', error);
        alert(`Erreur lors de la suppression: ${error.message || 'Erreur inconnue'}`);
      }
    }
  };

  const handleBlockSite = async () => {
    if (!selectedSite || !blockReason.trim()) {
      alert('Veuillez saisir une raison de blocage');
      return;
    }

    try {
      const success = await siteService.blockSite(selectedSite.id, blockReason);
      
      if (success) {
        setShowBlockModal(false);
        setSelectedSite(null);
        setBlockReason('');
        onSitesUpdate();
      } else {
        alert('Erreur lors du blocage du site');
      }
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(`Erreur lors du blocage du site: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const handleUnblockSite = async () => {
    if (!selectedSite) return;

    try {
      const success = await siteService.unblockSite(selectedSite.id);
      
      if (success) {
        setShowUnblockModal(false);
        setSelectedSite(null);
        onSitesUpdate();
      } else {
        alert('Erreur lors du déblocage du site');
      }
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(`Erreur lors du déblocage du site: ${error.message || 'Erreur inconnue'}`);
    }
  };

  const openBlockModal = (site: Site) => {
    setSelectedSite(site);
    setShowBlockModal(true);
  };

  const openUnblockModal = (site: Site) => {
    setSelectedSite(site);
    setShowUnblockModal(true);
  };

  const startEdit = (site: Site) => {
    setEditingSite(site);
    setFormData({
      name: site.name,
      code: site.code,
      site_type: site.site_type,
      timezone: site.timezone,
      address: site.address || '',
      contact_phone: site.contact_phone || '',
      contact_email: site.contact_email || '',
      tenant: site.tenant
    });
    setLogoFile(null);
    setLogoPreview(site.logo || null);
  };

  const getTenantName = (tenantId: number) => {
    const tenant = tenantsArray.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Tenant inconnu';
  };

  const renderForm = (isEdit = false) => (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">
            {isEdit ? 'Modifier le Site' : 'Créer un Nouveau Site'}
          </h3>
          
          <form onSubmit={isEdit ? handleUpdateSite : handleCreateSite} className="space-y-6">
            {/* Première ligne : Tenant (obligatoire) */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tenant propriétaire *
              </label>
              <select
                required
                value={formData.tenant}
                onChange={(e) => setFormData({...formData, tenant: parseInt(e.target.value)})}
                className="custom-select"
              >
                <option value={0}>Sélectionnez un tenant</option>
                {tenantsArray.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.code})
                  </option>
                ))}
              </select>
              <p className="mt-1 text-sm text-gray-500">
                Un site doit obligatoirement appartenir à un tenant
              </p>
            </div>

            {/* Deuxième ligne : Nom et Code */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom du site *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="custom-form-field"
                  placeholder="Nom du site"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Code du site *
                </label>
                <input
                  type="text"
                  required
                  value={formData.code}
                  onChange={(e) => setFormData({...formData, code: e.target.value})}
                  className="custom-form-field"
                  placeholder="Code du site"
                />
              </div>
            </div>
            
            {/* Logo du site */}
            <div className="flex justify-center">
              <LogoUpload
                currentLogo={logoPreview}
                onLogoChange={handleLogoChange}
                size="md"
                className="max-w-xs"
              />
            </div>
            
            {/* Troisième ligne : Type et Fuseau horaire */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de site
                </label>
                <select
                  value={formData.site_type}
                  onChange={(e) => setFormData({...formData, site_type: e.target.value})}
                  className="custom-select"
                >
                  <option value="STANDARD">Site standard</option>
                  <option value="INDUSTRIAL">Site industriel</option>
                  <option value="MOBILE">Site mobile</option>
                  <option value="WAREHOUSE">Entrepôt</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fuseau horaire
                </label>
                <input
                  type="text"
                  value={formData.timezone}
                  onChange={(e) => setFormData({...formData, timezone: e.target.value})}
                  className="custom-form-field"
                  placeholder="Fuseau horaire"
                />
              </div>
            </div>
            
            {/* Quatrième ligne : Adresse */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse complète
              </label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                rows={3}
                className="custom-textarea"
                placeholder="Adresse complète"
              />
            </div>
            
            {/* Cinquième ligne : Contact téléphone et email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Téléphone de contact
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  className="custom-form-field"
                  placeholder="Téléphone de contact"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Email de contact
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className="custom-form-field"
                  placeholder="Email de contact"
                />
              </div>
            </div>
            
                         {/* Boutons d'action */}
             <div className="flex space-x-4 pt-6 border-t border-gray-200">
               <button
                 type="submit"
                 disabled={isSubmitting}
                 className="flex-1 bg-orange-600 text-white px-6 py-3 rounded-md hover:bg-orange-700 disabled:opacity-50 font-medium transition-colors"
                 style={{
                   backgroundColor: isSubmitting ? '#9ca3af' : '#ea580c',
                   color: '#ffffff',
                   padding: '12px 24px',
                   borderRadius: '6px',
                   fontSize: '14px',
                   fontWeight: '500',
                   transition: 'all 0.2s ease-in-out',
                   cursor: isSubmitting ? 'not-allowed' : 'pointer'
                 }}
               >
                 {isSubmitting ? 'Enregistrement...' : (isEdit ? 'Modifier le site' : 'Créer le site')}
               </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false);
                  setEditingSite(null);
                }}
                className="flex-1 bg-gray-300 text-gray-700 px-6 py-3 rounded-md hover:bg-gray-400 font-medium transition-colors"
                style={{
                  backgroundColor: '#d1d5db',
                  color: '#374151',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.2s ease-in-out',
                  cursor: 'pointer'
                }}
              >
                Annuler
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <Building2 className="w-6 h-6 text-orange-500 mr-3" />
          <h2 className="text-xl font-semibold text-gray-900">Gestion des Sites</h2>
        </div>
        <PermissionButton
          permission={PERMISSIONS.SITE_MANAGEMENT_CREATE}
          onClick={() => setShowCreateForm(true)}
          className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700 flex items-center"
          disabledMessage="Permission insuffisante pour créer un site"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau Site
        </PermissionButton>
      </div>

      {/* Liste des sites */}
      {sitesArray.length === 0 && (
        <div className="text-center py-12">
          <Building2 className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun site créé</h3>
          <p className="text-gray-600 mb-4">
            Commencez par créer votre premier site pour organiser vos opérations.
          </p>
          <PermissionButton
            permission={PERMISSIONS.SITE_MANAGEMENT_CREATE}
            onClick={() => setShowCreateForm(true)}
            className="bg-orange-600 text-white px-4 py-2 rounded-md hover:bg-orange-700"
            disabledMessage="Permission insuffisante pour créer un site"
          >
            Créer le premier site
          </PermissionButton>
        </div>
      )}

      {sitesArray.length > 0 && (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {sitesArray.map((site) => (
            <div key={site.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-md transition-shadow">
              {/* En-tête avec nom et actions */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center">
                  <Building2 className="w-5 h-5 text-gray-400 mr-2" />
                  <h3 className="font-medium text-gray-900">{site.name}</h3>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => {}} // setViewingSite(site)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                    title="Voir les détails"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => startEdit(site)}
                    className="p-1 text-gray-400 hover:text-blue-600"
                    title="Modifier"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteSite(site.id)}
                    className="p-1 text-gray-400 hover:text-red-600"
                    title="Supprimer"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Code et type */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Code:</span>
                  <span className="text-sm text-gray-900 bg-gray-100 px-2 py-1 rounded">{site.code}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-700">Type:</span>
                  <span className="text-sm text-gray-900">{site.site_type}</span>
                </div>
              </div>

              {/* Tenant propriétaire */}
              <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center">
                  <Building2 className="w-4 h-4 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-800">Tenant propriétaire:</span>
                </div>
                <div className="ml-6 mt-1">
                  <span className="text-sm text-blue-900">{getTenantName(site.tenant)}</span>
                </div>
              </div>

              {/* Informations de contact */}
              {(site.address || site.contact_phone || site.contact_email) && (
                <div className="mb-4 space-y-2">
                  {site.address && (
                    <div className="flex items-start text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{site.address}</span>
                    </div>
                  )}
                  {site.contact_phone && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Tél:</span>
                      <span className="text-gray-700">{site.contact_phone}</span>
                    </div>
                  )}
                  {site.contact_email && (
                    <div className="flex items-center text-sm text-gray-600">
                      <span className="font-medium mr-2">Email:</span>
                      <span className="text-gray-700 truncate">{site.contact_email}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Statut et actions de blocage */}
              <div className="border-t border-gray-200 pt-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700 mr-2">Statut:</span>
                    {site.is_blocked ? (
                      <span className="flex items-center text-red-600 text-sm">
                        <Lock className="w-4 h-4 mr-1" />
                        Bloqué
                      </span>
                    ) : (
                      <span className="flex items-center text-green-600 text-sm">
                        <CheckCircle className="w-4 h-4 mr-1" />
                        Actif
                      </span>
                    )}
                  </div>
                  
                  <div className="flex space-x-2">
                    {site.is_blocked ? (
                      <button
                        onClick={() => openUnblockModal(site)}
                        className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                        title="Débloquer le site"
                      >
                        <Unlock className="w-3 h-3 inline mr-1" />
                        Débloquer
                      </button>
                    ) : (
                      <button
                        onClick={() => openBlockModal(site)}
                        className="px-3 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 transition-colors"
                        title="Bloquer le site"
                      >
                        <Lock className="w-3 h-3 inline mr-1" />
                        Bloquer
                      </button>
                    )}
                  </div>
                </div>
                
                {site.is_blocked && site.block_reason && (
                  <div className="text-sm text-red-600 bg-red-50 p-2 rounded border border-red-200">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    <span className="font-medium">Raison:</span> {site.block_reason}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreateForm && renderForm(false)}
      {editingSite && renderForm(true)}
      
      {/* Modal de visualisation */}
      {/* {viewingSite && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Détails du Site</h3>
              <div className="space-y-3">
                <div>
                  <span className="font-medium text-gray-700">Nom:</span>
                  <p className="text-gray-900">{viewingSite.name}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Code:</span>
                  <p className="text-gray-900 font-mono">{viewingSite.code}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Type:</span>
                  <p className="text-gray-900">{getSiteTypeLabel(viewingSite.site_type)}</p>
                </div>
                <div>
                  <span className="font-medium text-gray-700">Tenant:</span>
                  <p className="text-gray-900">{viewingSite.tenant.name} ({viewingSite.tenant.code})</p>
                </div>
                {viewingSite.address && (
                  <div>
                    <span className="font-medium text-gray-700">Adresse:</span>
                    <p className="text-gray-900">{viewingSite.address}</p>
                  </div>
                )}
                {viewingSite.contact_email && (
                  <div>
                    <span className="font-medium text-gray-700">Email:</span>
                    <p className="text-gray-900">{viewingSite.contact_email}</p>
                  </div>
                )}
                {viewingSite.contact_phone && (
                  <div>
                    <span className="font-medium text-gray-700">Téléphone:</span>
                    <p className="text-gray-900">{viewingSite.contact_phone}</p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Statut:</span>
                  <p className={`${viewingSite.is_blocked ? 'text-red-600' : viewingSite.is_active ? 'text-green-600' : 'text-red-600'}`}>
                    {viewingSite.is_blocked ? 'Bloqué' : viewingSite.is_active ? 'Actif' : 'Inactif'}
                  </p>
                </div>
                {viewingSite.is_blocked && viewingSite.block_reason && (
                  <div>
                    <span className="font-medium text-gray-700">Motif du blocage:</span>
                    <p className="text-red-700">{viewingSite.block_reason}</p>
                  </div>
                )}
                <div>
                  <span className="font-medium text-gray-700">Créé le:</span>
                  <p className="text-gray-900">{new Date(viewingSite.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              </div>
              <div className="mt-6">
                <button
                  onClick={() => setViewingSite(null)}
                  className="w-full bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        </div>
      )} */}

      {/* Modal de blocage */}
      {showBlockModal && selectedSite && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-red-900 mb-4">Bloquer le Site</h3>
              <p className="text-sm text-gray-600 mb-4">
                Êtes-vous sûr de vouloir bloquer le site <strong>{selectedSite.name}</strong> ?
              </p>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Motif du blocage *
                </label>
                <textarea
                  value={blockReason}
                  onChange={(e) => setBlockReason(e.target.value)}
                  rows={4}
                  className="custom-textarea"
                  placeholder="Expliquez la raison du blocage..."
                  required
                />
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleBlockSite}
                  disabled={!blockReason.trim()}
                  className="flex-1 bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                >
                  Bloquer
                </button>
                <button
                  onClick={() => {
                    setShowBlockModal(false);
                    setSelectedSite(null);
                    setBlockReason('');
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de déblocage */}
      {showUnblockModal && selectedSite && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[10000]">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-green-900 mb-4">Débloquer le Site</h3>
              <p className="text-sm text-gray-600 mb-4">
                Êtes-vous sûr de vouloir débloquer le site <strong>{selectedSite.name}</strong> ?
              </p>
              
              <div className="bg-green-50 p-3 rounded-md border border-green-200 mb-4">
                <div className="text-sm text-green-800">
                  <div className="font-medium mb-1">Motif actuel du blocage:</div>
                  <div className="text-green-700">{selectedSite.block_reason}</div>
                </div>
              </div>
              
              <div className="flex space-x-3 pt-4">
                <button
                  onClick={handleUnblockSite}
                  className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700"
                >
                  Débloquer
                </button>
                <button
                  onClick={() => {
                    setShowUnblockModal(false);
                    setSelectedSite(null);
                  }}
                  className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-400"
                >
                  Annuler
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SiteManagement;
